import { User } from "@/modules/user/types/user";
import { create } from "zustand";
import { getUser } from "../api/user/me";
import { Referral } from "@/modules/user/types/referral";
import { getMyWallet } from "../api/user/wallet";
import { getDeposit, getDeposits, getMinDeposit } from "../api/deposit";
import { getRewards, getWithdrawDepositRewards } from "../api/deposit/rewards";
import { getAirdrop } from "../api/deposit/airdrop";
import { getPrice } from "../api/price";
import { getTonUsdPrice } from "../api/price/tonUsd";
import { getReferralNetwork } from "../api/referral/network";
import { ReferralNetwork } from "@/modules/user/types/network";
import {
  DepositsItem,
  MatchingBonus,
  RankCode,
  WithdrawDepositRewards,
} from "@/modules/user/types/wallet";
import { VestingResponse } from "@/modules/user/types/vesting";
import { getVesting } from "../api/vesting";
import {
  BonusesSettings,
  BonusRequirement,
  BonusWeekData,
} from "@/modules/user/types/bonuses";
import { getBonusesWeek, getWithdrawWttBonusesInfo } from "../api/bonus/week";
import { getMatchingBonus } from "../api/deposit/matching-bonus";
import { PromoThailand } from "@/modules/user/types/promo-thailand";
import { getPromoThailand } from "../api/bonus/promo-thailand";

interface Balance {
  btn: number;
  usd: number;
  min?: number;
  minToken?: number;
}

interface BalanceRewards {
  btn: number;
  usd: number;
  min?: number;
  tomorrowRewards: number;
  totalRewardsBalance: number;
  totalRewardsBalanceUsd: number;
  nextStartTimeWithdraw: string;
  nextEndTimeWithdraw: string;
  withdrawable: boolean;
}

interface BalanceTon {
  ton: number;
  usd: number;
  fee?: number;
  minWithdraw?: number;
}

interface BalanceToken {
  token: number;
  usd: number;
  minWithdraw?: number;
}

interface Wallet {
  address: string;
  balance: number;
  jettonAddress?: string;
  jettonBalance?: number;
}

interface UserStore {
  user: User | null;
  isAuthLoading: boolean;
  refcode: string;
  invitedBy: string;
  price: number;
  tonPrice: number;
  deposit: Balance;
  isDepositLoading: boolean;
  deposits: DepositsItem[];
  rewards: BalanceRewards;
  withdrawDepositRewards: WithdrawDepositRewards;
  withdrawDepositRewardsChecked: WithdrawDepositRewards;
  matchingBonus: MatchingBonus;
  matchingBonusBASE: MatchingBonus;
  isMatchingBonusLoading: boolean;
  isMatchingBonusLoadingBASE: boolean;
  airdrop: BalanceTon;
  airdropBASE: BalanceToken;
  isAirdropLoading: boolean;
  isAirdropBASELoading: boolean;
  wallet: Wallet;
  activated: boolean;
  network: ReferralNetwork | null;
  isNetworkLoading: boolean;
  vesting: VestingResponse | null;
  requirements: BonusRequirement[];
  weeks: Record<number, BonusWeekData>;
  bonusesSettings: BonusesSettings;
  promoThailand: PromoThailand;
  isPromoThailandLoading: boolean;
  isReferralLoading: boolean;
  isRewardsLoading: boolean;
  setUser: (chainId: number) => Promise<void>;
  setUserOnly: () => Promise<void>;
  setIsAuthLoading: (value: boolean) => void;
  setReferral: (data: Referral) => void;
  setActivated: (activated: boolean) => void;
  setWalletQuery: () => Promise<void>;
  setDeposit: () => Promise<void>;
  setDeposits: () => Promise<void>;
  setMinDeposit: () => Promise<void>;
  setRewards: () => Promise<void>;
  setWithdrawDepositRewards: () => Promise<void>;
  setWithdrawDepositRewardsChecked: (data: WithdrawDepositRewards) => void;
  setWithdrawDepositRewardsCheckedEmpty: () => void;
  setMatchingBonus: () => Promise<void>;
  setMatchingBonusBASE: (chainId: number) => Promise<void>;
  setAirdrop: () => Promise<void>;
  setAirdropBASE: (chainId: number) => Promise<void>;
  setWalletBalance: (balance: number) => void;
  setWallet: (address: string) => void;
  startPolling: (chainId: number) => void;
  stopPolling: () => void;
  setNetwork: () => Promise<void>;
  setVesting: () => Promise<void>;
  setBonuses: (weekOffset: number) => Promise<void>;
  setSelectedWeek: (selectedWeek: number) => void;
  setLastEventNow: () => void;
  setPromoThailand: () => Promise<void>;
}

let pollingInterval: NodeJS.Timeout | null = null;
let pollingInterval5mins: NodeJS.Timeout | null = null;

export const useUserStore = create<UserStore>((set, get) => ({
  user: null,
  isAuthLoading: true,
  refcode: "",
  invitedBy: "",
  price: 1,
  tonPrice: 0,
  deposit: {
    btn: 0,
    usd: 0,
    min: 1,
  },
  isDepositLoading: true,
  deposits: [],
  rewards: {
    btn: 0,
    usd: 0,
    tomorrowRewards: 0,
    totalRewardsBalance: 0,
    totalRewardsBalanceUsd: 0,
    nextStartTimeWithdraw: "",
    nextEndTimeWithdraw: "",
    withdrawable: false,
  },
  withdrawDepositRewards: {
    totalTokenAmount: 0,
    totalRewardsBalanceUsd: 0,
    nextStartTimeWithdraw: "",
    nextEndTimeWithdraw: "",
    depositsRewards: [],
    existingTokenAmount: 0,
  },
  withdrawDepositRewardsChecked: {
    totalTokenAmount: 0,
    totalRewardsBalanceUsd: 0,
    nextStartTimeWithdraw: "",
    nextEndTimeWithdraw: "",
    depositsRewards: [],
    existingTokenAmount: 0,
  },
  matchingBonus: {
    tokenAmount: 0,
    usdAmount: 0,
    tonAmount: 0,
    nextStartTimeWithdraw: "",
    nextEndTimeWithdraw: "",
    bonuses: [],
    rank: {
      current: {
        code: "BRONZE" as RankCode,
        title: "Bronze",
        rewards: {
          minPercent: 0,
          maxPercent: 0,
          generations: 0,
          payouts: [],
        },
      },
      next: {
        code: "BRONZE",
        title: "Bronze",
        rewards: {
          minPercent: 0,
          maxPercent: 0,
          generations: 0,
          payouts: [],
        },
        progress: {
          activeDirects: { current: 0, required: 0, percentage: 0 },
          activeInFirstNLevels: { current: 0, required: 0, percentage: 0 },
          personalVolume: { current: 0, required: 0, percentage: 0 },
        },
      },
    },
    promoTurnover: 0,
  },
  matchingBonusBASE: {
    tokenAmount: 0,
    usdAmount: 0,
    tonAmount: 0,
    nextStartTimeWithdraw: "",
    nextEndTimeWithdraw: "",
    bonuses: [],
    rank: {
      current: {
        code: "BRONZE" as RankCode,
        title: "Bronze",
        rewards: {
          minPercent: 0,
          maxPercent: 0,
          generations: 0,
          payouts: [],
        },
      },
      next: {
        code: "BRONZE",
        title: "Bronze",
        rewards: {
          minPercent: 0,
          maxPercent: 0,
          generations: 0,
          payouts: [],
        },
        progress: {
          activeDirects: { current: 0, required: 0, percentage: 0 },
          activeInFirstNLevels: { current: 0, required: 0, percentage: 0 },
          personalVolume: { current: 0, required: 0, percentage: 0 },
        },
      },
    },
    promoTurnover: 0,
  },
  isMatchingBonusLoading: true,
  isMatchingBonusLoadingBASE: true,
  airdrop: {
    ton: 0,
    usd: 0,
    fee: 0.02,
    minWithdraw: 0,
  },
  airdropBASE: {
    token: 0,
    usd: 0,
    fee: 0.02,
    minWithdraw: 0,
  },
  isAirdropLoading: true,
  isAirdropBASELoading: false,
  wallet: {
    address: "",
    balance: 0,
  },
  activated: false,
  network: null,
  isNetworkLoading: true,
  vesting: null,
  requirements: [],
  weeks: [],
  bonusesSettings: {
    weeksNumber: 0,
    selectedWeek: 0,
    withdrawInfo: {
      nextStartTimeWithdraw: "",
      nextEndTimeWithdraw: "",
      totalUsdAmount: 0,
      totalTonAmount: 0,
      existingWttBonuses: [],
    },
  },
  promoThailand: {
    promoTurnover1: 0,
    promoTurnover10: 0,
  },
  isPromoThailandLoading: true,
  isReferralLoading: true,
  isRewardsLoading: true,
  setUser: async (chainId) => {
    const data = await getUser();

    if (!data) return;

    const price = await getPrice();

    const tonPrice = await getTonUsdPrice();

    if (!price || !tonPrice) return;

    set({ price, tonPrice });

    const {
      setWalletQuery,
      setDeposit,
      setDeposits,
      setRewards,
      setWithdrawDepositRewards,
      setMatchingBonus,
      setMatchingBonusBASE,
      setAirdrop,
      setAirdropBASE,
      setNetwork,
      setVesting,
      setPromoThailand,
      startPolling,
    } = get();

    set({ user: data });

    setWalletQuery();
    setDeposit();
    setDeposits();
    setRewards();
    setWithdrawDepositRewards();
    setMatchingBonus();
    setMatchingBonusBASE(chainId);
    setAirdrop();
    setAirdropBASE(chainId);
    setNetwork();
    setVesting();
    setPromoThailand();

    startPolling(chainId);
  },
  setUserOnly: async () => {
    const data = await getUser();

    if (!data) return;

    set({ user: data });
  },
  setIsAuthLoading: (value: boolean) => set({ isAuthLoading: value }),
  setReferral: ({ invitedBy, userReferralCode, activeStatus }: Referral) => {
    set({
      refcode: userReferralCode,
      invitedBy: invitedBy,
      activated: activeStatus,
      isReferralLoading: false,
    });
  },
  setActivated: (activated: boolean) => {
    set({ activated });
  },
  setWalletQuery: async () => {
    const data = await getMyWallet();

    const wallet = data?.walletAddress;
    const jettonAddress = data?.jettonAddress;
    const jettonBalance = data?.jettonBalance;

    set((state) => ({
      wallet: {
        ...state.wallet,
        address: wallet || "",
        jettonAddress: jettonAddress || "",
        jettonBalance: jettonBalance || 0,
      },
    }));
  },
  setDeposit: async () => {
    const data = await getDeposit();

    if (!data) return;

    const btn = data.totalDeposit;

    const { price, deposit } = get();

    set({
      deposit: {
        ...deposit,
        btn,
        usd: btn * price,
      },
      isDepositLoading: false,
    });
  },
  setDeposits: async () => {
    const data = await getDeposits();

    if (!data) return;

    const deposits = data.deposits;

    set({
      deposits,
    });
  },
  setMinDeposit: async () => {
    const data = await getMinDeposit();

    if (!data) return;

    const min = data.minTonAmount;
    const minToken = data.minTokenAmount;

    const { deposit } = get();

    set({
      deposit: {
        ...deposit,
        min,
        minToken,
      },
    });
  },
  setRewards: async () => {
    const data = await getRewards();

    if (!data) return;

    const btn = data.totalRewardsBalance;

    const { price } = get();

    const nextStartTimeWithdraw = data.nextStartTimeWithdraw;

    const nextEndTimeWithdraw = data.nextEndTimeWithdraw;

    const withdrawable = data.withdrawable;

    set({
      rewards: {
        btn,
        usd: btn * price,
        tomorrowRewards: data.tomorrowRewards,
        totalRewardsBalance: data.totalRewardsBalance,
        totalRewardsBalanceUsd: data.totalRewardsBalanceUsd,
        nextStartTimeWithdraw,
        nextEndTimeWithdraw,
        withdrawable,
      },
      isRewardsLoading: false,
    });
  },
  setWithdrawDepositRewards: async () => {
    const data = await getWithdrawDepositRewards();

    if (!data) return;

    set({
      withdrawDepositRewards: data,
    });
  },
  setWithdrawDepositRewardsChecked: (data: WithdrawDepositRewards) => {
    if (!data) return;

    set({
      withdrawDepositRewardsChecked: data,
    });
  },
  setWithdrawDepositRewardsCheckedEmpty: () => {
    set({
      withdrawDepositRewardsChecked: {
        totalTokenAmount: 0,
        nextStartTimeWithdraw: "",
        nextEndTimeWithdraw: "",
        depositsRewards: [],
        existingTokenAmount: 0,
      },
    });
  },
  setMatchingBonus: async () => {
    const data = await getMatchingBonus("TON", 0);

    if (!data) return;

    set({
      matchingBonus: data,
      isMatchingBonusLoading: false,
    });
  },
  setMatchingBonusBASE: async (chainId) => {
    const data = await getMatchingBonus("EVM", chainId);

    if (!data) return;

    set({
      matchingBonusBASE: data,
      isMatchingBonusLoadingBASE: false,
    });
  },
  setAirdrop: async () => {
    const data = await getAirdrop("TON", 0);

    if (!data) return;

    const ton = data.totalAirdropBalance;

    const { tonPrice } = get();

    set({
      airdrop: {
        ton,
        usd: ton * tonPrice,
        fee: data.transactionFee || 0.02,
        minWithdraw: 1, // data.minWithdraw ||
      },
      isAirdropLoading: false,
    });
  },
  setAirdropBASE: async (chainId) => {
    const data = await getAirdrop("EVM", chainId);

    if (!data) return;

    const token = data.totalAirdropBalance;

    const { price } = get();

    set({
      airdropBASE: {
        token,
        usd: token * price,
        minWithdraw: 1,
      },
      isAirdropBASELoading: false,
    });
  },
  setWalletBalance: (balance: number) => {
    const { wallet } = get();

    set({
      wallet: {
        ...wallet,
        balance: balance,
      },
    });
  },
  setWallet: (address: string) => {
    const { wallet } = get();

    set({
      wallet: {
        ...wallet,
        address,
      },
    });
  },
  startPolling: (chainId) => {
    if (pollingInterval) return;

    const {
      setDeposit,
      setDeposits,
      setRewards,
      setWithdrawDepositRewards,
      setMatchingBonus,
      setMatchingBonusBASE,
      setAirdrop,
      setAirdropBASE,
      setNetwork,
      setVesting,
    } = get();

    pollingInterval = setInterval(() => {
      setWithdrawDepositRewards();
      setDeposit();
      setDeposits();
      setAirdrop();
      setAirdropBASE(chainId);
      setNetwork();
    }, 60000);

    pollingInterval5mins = setInterval(() => {
      setMatchingBonus();
      setMatchingBonusBASE(chainId);
      setVesting();
      setRewards();
    }, 300000);
  },
  stopPolling: () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;

      if (pollingInterval5mins) {
        clearInterval(pollingInterval5mins);
        pollingInterval5mins = null;
      }
    }
  },
  setNetwork: async () => {
    set({ isNetworkLoading: true });
    const data = await getReferralNetwork();

    if (!data) {
      return;
    }

    set({ network: data });
    setTimeout(() => {
      set({ isNetworkLoading: false });
    }, 500);
  },
  setVesting: async () => {
    const data = await getVesting();

    if (!data) return;

    set({ vesting: data });
  },
  setBonuses: async (weekOffset: number) => {
    const data = await getBonusesWeek(weekOffset);

    const dataWithdrawInfo = await getWithdrawWttBonusesInfo();

    if (!data || !dataWithdrawInfo) return;

    set((state) => ({
      requirements: data.requirements,
      weeks: {
        ...state.weeks,
        [weekOffset]: data.week,
      },
      bonusesSettings: {
        weeksNumber: data.weeksNumber,
        selectedWeek: weekOffset,
        withdrawInfo: dataWithdrawInfo,
      },
    }));
  },
  setSelectedWeek: (selectedWeek: number) => {
    set((state) => ({
      bonusesSettings: {
        ...state.bonusesSettings,
        selectedWeek: selectedWeek,
      },
    }));
  },
  setLastEventNow: () => {
    const { user } = get();
    if (!user) return;

    const updatedUser = {
      ...user,
      lastEventAt: new Date().toISOString(),
    };

    set({ user: updatedUser });
  },
  setPromoThailand: async () => {
    const data = await getPromoThailand();

    if (!data) return;

    set({ promoThailand: data, isPromoThailandLoading: false });
  },
}));
